import React, { useEffect, useMemo, useState } from 'react';
import { Row, Col, Card, CardBody, CardTitle, Button, ButtonGroup, Spinner } from 'reactstrap';
import { useMutation, useQueryClient } from 'react-query';
import { useRole } from 'hooks/useRole';
import moment from 'moment';
import { useAuth } from 'hooks/useAuth';
import Apaexlinecolumn from '../../AllCharts/apex/apaexlinecolumn';
import Breadcrumbs from '../../../components/Common/Breadcrumb';
import MiniWidget from './mini-widget';
import { useSelector, useDispatch } from 'react-redux';
import LineApexChart from '../../AllCharts/apex/chartapex';
import { getPaymentsStatusCountsApi } from 'api/report';
import { setMemberSummary, setPaymentSummary } from 'store/actions';
import { getSearchColumns, sumArray } from 'utils/tools';
import MainFilter from 'components/Common/MainFilter';

//Members Chart
const series1 = [
  { name: 'Members', data: [12, 14, 2, 47, 42, 15, 47, 75, 65, 19, 14] },
];
const options1 = {
  chart: { sparkline: { enabled: !0 } },
  stroke: { curve: 'smooth', width: 2 },
  colors: ['#f1b44c'],
  fill: {
    type: 'gradient',
    gradient: {
      shadeIntensity: 1,
      inverseColors: false,
      opacityFrom: 0.45,
      opacityTo: 0.05,
      stops: [25, 100, 100, 100],
    },
  },
  tooltip: {
    fixed: { enabled: false },
    x: { show: false },
    marker: { show: false },
  },
};

//Dependants Chart
const series2 = [
  { name: 'Dependants', data: [25, 66, 41, 89, 63, 25, 44, 12, 36, 9, 54] },
];
const options2 = {
  chart: { sparkline: { enabled: !0 } },
  stroke: { curve: 'smooth', width: 2 },
  colors: ['#00b3e4'],
  fill: {
    type: 'gradient',
    gradient: {
      shadeIntensity: 1,
      inverseColors: false,
      opacityFrom: 0.45,
      opacityTo: 0.05,
      stops: [25, 100, 100, 100],
    },
  },
  tooltip: {
    fixed: { enabled: false },
    x: { show: false },
    marker: { show: false },
  },
};

//Defaulted Chart
const series3 = [
  { name: 'Defaulted', data: [35, 53, 93, 47, 54, 24, 47, 75, 65, 19, 14] },
];
const options3 = {
  chart: { sparkline: { enabled: !0 } },
  stroke: { curve: 'smooth', width: 2 },
  colors: ['#50a5f1'],
  fill: {
    type: 'gradient',
    gradient: {
      shadeIntensity: 1,
      inverseColors: false,
      opacityFrom: 0.45,
      opacityTo: 0.05,
      stops: [25, 100, 100, 100],
    },
  },
  tooltip: {
    fixed: { enabled: false },
    x: { show: false },
    marker: { show: false },
  },
};

const PaymentReports = () => {
  const { isAuthenticated } = useAuth();
  const {
    isFacilitator,
    isSiteCord,
    isCountyCoordinator,
    isClusterLeader,
    isFinance,
    isDirector,
    addresses,
  } = useRole();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const { paymentSummary } = useSelector((state) => ({
    paymentSummary: state.reports.payments,
  }));

  const reports = [
    {
      title: 'Total Premiums',
      icon: 'mdi mdi-account',
      color: 'primary',
      value: paymentSummary?.length
        ? sumArray(paymentSummary, 'totalPremiums')?.toLocaleString() || 0
        : 0,
      desc: 'Total Premiums',
      series: series1,
      options: options1,
      arrowUpDown: 'mdi mdi-arrow-up ms-1 text-success',
    },
    {
      title: 'Successful Transactions',
      icon: 'mdi mdi-check-circle',
      color: 'success',
      value: paymentSummary?.length
        ? sumArray(paymentSummary, 'successfulTransactions')?.toLocaleString() || 0
        : 0,
      desc: 'Successful Transactions',
      series: series1,
      options: options1,
      arrowUpDown: 'mdi mdi-arrow-up ms-1 text-success',
    },
    {
      title: 'Allocated',
      icon: 'mdi mdi-account-check',
      color: 'warning',
      value: paymentSummary?.length
        ? sumArray(paymentSummary, 'allocated')?.toLocaleString() || 0
        : 0,
      desc: 'Allocated Members',
      series: series3,
      options: options3,
      arrowUpDown: 'mdi mdi-arrow-up ms-1 text-success',
    },
  ];

  const [timePeriod, setTimePeriod] = useState('year');

  const columnChartSeries = useMemo(() => {
    if (timePeriod === 'year') {
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const currentMonthIndex = moment().month();
      const yearData = months.slice(0, currentMonthIndex + 1).map((month) => {
        const summary = paymentSummary?.find((s) => s.monthName === month) || {};
        return {
          totalPremiums: summary.totalPremiums || 0,
          successfulTransactions: summary.successfulTransactions || 0,
          allocated: summary.allocated || 0,
        };
      });
      return [
        { name: 'Total Premiums', data: yearData.map((d) => d.totalPremiums) },
        { name: 'Successful Transactions', data: yearData.map((d) => d.successfulTransactions) },
        { name: 'Allocated', data: yearData.map((d) => d.allocated) },
      ];
    }

    const currentMonthData = paymentSummary?.filter((s) => s.monthName === moment().format('MMM')) || [];
    return [
      { name: 'Total Premiums', data: currentMonthData.map((s) => s.totalPremiums || 0) },
      { name: 'Successful Transactions', data: currentMonthData.map((s) => s.successfulTransactions || 0) },
      { name: 'Allocated', data: currentMonthData.map((s) => s.allocated || 0) },
    ];
  }, [paymentSummary, timePeriod]);

  const categories = useMemo(() => {
    if (timePeriod === 'year') {
      return ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].slice(
        0,
        moment().month() + 1
      );
    }
    return [moment().format('MMM')];
  }, [timePeriod]);

  const {
    mutate: mutatePaymentsCounts,
    isLoading: isLoadingProducts,
    data,
  } = useMutation((payload) => getPaymentsStatusCountsApi(payload), {
    onSuccess: async (res) => {
      await dispatch(setPaymentSummary(res?.data));
    },
    onSettled: () => {
      queryClient.invalidateQueries('get-all-counties');
    },
  });

  useEffect(() => {
    const fromDate =
      timePeriod === 'year'
        ? moment().startOf('year').format('DD-MM-YYYY')
        : moment().startOf('month').format('DD-MM-YYYY');
    const toDate = moment().format('DD-MM-YYYY');
    mutatePaymentsCounts({
      fromDate,
      toDate,
      address: {},
    });
  }, [timePeriod]);

  const onSearch = (data) => {
    const addressData = {};

    // Role-based address filtering
    if (isFacilitator && addresses?.wardId) {
      addressData['wardId'] = addresses?.wardId;
    }
    if (isSiteCord && data?.wardId) {
      addressData['wardId'] = data?.wardId.value;
    }
    if (data?.wardId) {
      addressData['wardId'] = data?.wardId.value;
    }
    if (data?.constituencyId) {
      addressData['constituencyId'] = data?.constituencyId.value;
    }
    if (data?.countyId) {
      addressData['countyId'] = data?.countyId.value;
    }
    if (data?.regionId) {
      addressData['regionId'] = data?.regionId.value;
    }
  
    const fromDate =
      data?.startDate?.value ||
      (timePeriod === 'year'
        ? moment().startOf('year').format('DD-MM-YYYY')
        : moment().startOf('month').format('DD-MM-YYYY'));
    const toDate = data?.endDate?.value || moment().format('DD-MM-YYYY');
  
    const payload = {
      fromDate,
      toDate,
      wardIds: getSearchColumns()?.find((t) => t.name === 'wardIds')?.value,
      customDate: !!(data?.startDate?.value || data?.endDate?.value),
      address: addressData,
    };
    mutatePaymentsCounts(payload);
  };

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs title="Reports" breadcrumbItem="Payment Reports" />
        <MainFilter onSearch={onSearch} showDateFilter />
        <Row>
          <MiniWidget reports={reports} />
        </Row>
        <Row>
          <Col md={8}>
            <Card>
              <CardBody>
                <CardTitle className="mb-4">Payment Summary</CardTitle>
                <ButtonGroup className="mb-3">
                  <Button
                    color={timePeriod === 'year' ? 'primary' : 'secondary'}
                    onClick={() => setTimePeriod('year')}
                  >
                    Year
                  </Button>
                  <Button
                    color={timePeriod === 'month' ? 'primary' : 'secondary'}
                    onClick={() => setTimePeriod('month')}
                  >
                    Month
                  </Button>
                </ButtonGroup>
                {isLoadingProducts ? (
                  // Step 2: Added Spinner for loading state
                  <div className="text-center">
                    <Spinner color="primary" />
                  </div>
                ) : paymentSummary && paymentSummary.length > 0 ? (
                  <Apaexlinecolumn
                    dataColors='["--bs-primary","--bs-success","--bs-warning"]'
                    series={columnChartSeries}
                    categories={categories}
                  />
                ) : (
                  <p>No data available</p>
                )}
              </CardBody>
            </Card>
          </Col>
          <Col md={4}>
            <Card>
              <CardBody>
                <CardTitle className="mb-4">New Members</CardTitle>
                <LineApexChart dataColors='["--bs-primary","--bs-success"]' />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default PaymentReports;
